/**
 * GalleryList — the public index of photo galleries at /gallery.
 *
 * NO FETCH: the source is the build-time snapshot `cmsGalleries` written by
 * `npm run cms:sync`. That is what makes the page prerenderable — the
 * static HTML a crawler (or a reader without JavaScript) receives already
 * contains every card.
 *
 * Locale handling follows the rest of the site: the URL carries the locale
 * and `i18n.language` is already the detected locale by the time this page
 * renders. Gallery copy is a per-locale record on the entry, so it is picked
 * here rather than through i18next; the page chrome (headings, labels) comes
 * from the `gallery` i18n namespace.
 */
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Container, PageHeader, Reveal } from '@/components/ui'
import { H2, H3 } from '@/components/ui/typography'
import CmsImage from '@/components/media/CmsImage'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import { cmsGalleries } from '@/content/cms/galleries.generated'
import type { GalleryEntry, GalleryLocale } from '@/content/gallery-types'

/**
 * Pick the locale-appropriate string from a per-locale record.
 *
 * Empty is "not filled in": the operator may have translated only some
 * locales, so the chain walks to the default locale rather than showing a
 * blank heading. English falls back to Traditional Chinese — the site's
 * primary audience — before Simplified.
 */
function pickLocalized(
  field: Record<GalleryLocale, string>,
  locale: GalleryLocale,
): string {
  return (
    field[locale]?.trim() ||
    field['zh-Hant']?.trim() ||
    (locale === 'en' ? field['zh-Hans']?.trim() : '') ||
    field.en?.trim() ||
    field['zh-Hans']?.trim() ||
    ''
  )
}

/** Normalise i18next's language onto one of the gallery locales. */
function asGalleryLocale(language: string | undefined): GalleryLocale {
  if (language === 'zh-Hans' || language === 'en') return language
  return 'zh-Hant'
}

/** `event_date` is a CMS date string; render it in the reader's locale. */
function formatEventDate(value: string | null, locale: GalleryLocale): string | null {
  if (!value) return null
  const time = Date.parse(value)
  if (!Number.isFinite(time)) return value
  return new Date(time).toLocaleDateString(locale === 'en' ? 'en-HK' : 'zh-HK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Newest first. `event_date` is the date the operator set for the activity;
 * `published_at` (a timestamp) is the fallback for rows without one. Galleries
 * with neither sink to the end. JavaScript's sort is stable, so ties keep the
 * server's display order.
 */
function sortedGalleries(entries: GalleryEntry[]): GalleryEntry[] {
  const timeOf = (g: GalleryEntry): number => {
    const event = g.event_date ? Date.parse(g.event_date) : Number.NaN
    if (Number.isFinite(event)) return event
    return g.published_at ?? Number.NEGATIVE_INFINITY
  }
  return [...entries].sort((a, b) => timeOf(b) - timeOf(a))
}

export default function GalleryList() {
  const { t, i18n } = useTranslation()
  const locale = asGalleryLocale(i18n.language)
  const galleries = sortedGalleries(cmsGalleries)

  return (
    <>
      <SEO
        title={t('seo.galleryTitle')}
        description={t('seo.galleryDescription')}
        keywords={t('seo.galleryKeywords')}
        jsonLd={buildBreadcrumbList([
          { name: t('nav.home'), path: '/' },
          { name: t('gallery.navLabel'), path: '/gallery' },
        ])}
      />

      <PageHeader
        tag={t('gallery.navLabel')}
        title={t('gallery.title')}
        description={t('gallery.subtitle')}
      />

      <section
        className="py-section-y px-section-x bg-surface"
        aria-labelledby="gallery-archive-title"
      >
        <Container size="xl">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-7">
              <span className="section-title__tagline inline-block mb-3">
                {t('gallery.navLabel')}
              </span>
              <H2 id="gallery-archive-title">{t('gallery.archiveTitle')}</H2>
            </div>
          </Reveal>

          {galleries.length === 0 ? (
            <Reveal>
              <p className="text-center text-ink-muted py-12">{t('gallery.empty')}</p>
            </Reveal>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-gap-cards">
              {galleries.map((gallery, index) => {
                const title = pickLocalized(gallery.title, locale) || gallery.slug
                const dateLabel = formatEventDate(gallery.event_date, locale)

                return (
                  <Reveal as="li" key={gallery.slug} delay={index * 0.05}>
                    <Link
                      to={`/gallery/${gallery.slug}`}
                      aria-label={title}
                      className="group block h-full focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-xl"
                    >
                      <article className="h-full overflow-hidden rounded-yada-xl border border-border bg-surface shadow-yada transition-shadow group-hover:shadow-yada-lg">
                        {gallery.cover_media_uuid && (
                          <CmsImage
                            uuid={gallery.cover_media_uuid}
                            alt={title}
                            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            className="block w-full aspect-[16/10] object-cover"
                          />
                        )}
                        <div className="p-5">
                          {dateLabel && (
                            <p className="text-xs text-ink-muted mb-1">
                              <time dateTime={gallery.event_date ?? undefined}>{dateLabel}</time>
                            </p>
                          )}
                          <H3 className="line-clamp-2 mb-2">{title}</H3>
                          <p className="text-sm text-ink-soft">
                            {t('gallery.photoCount', { count: gallery.photos.length })}
                          </p>
                        </div>
                      </article>
                    </Link>
                  </Reveal>
                )
              })}
            </ul>
          )}
        </Container>
      </section>
    </>
  )
}
